import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { App } from './App';
import { ErrorBoundary, RouteBoundary } from './components/error-boundary';
import { SiteNew } from './routes/site-new';
import { PageEdit } from './routes/page-edit';
import { Billing } from './routes/billing';
import { Claim } from './routes/claim';
import { InviteAccept } from './routes/invite';
import { SurveysList } from './features/surveys/surveys-list';
import { NotFound } from './routes/not-found';
import { AppIndex } from './routes/app-index';
import { LegacyRedirect } from './components/legacy-redirect';
import { BusinessLayout } from './components/panel-shell';
import { Overview } from './routes/overview';
import { Setup } from './routes/setup';
import { LayerLanding } from './routes/layer-landing';
import { Website } from './routes/foundation/website';
import { BusinessInfo } from './routes/foundation/business-info';
import { Mailboxes } from './routes/foundation/mailboxes';
import { DomainWizard } from './routes/foundation/domain-wizard';
import { GetFoundReady } from './routes/foundation/get-found-ready';
import { GoogleProfile } from './routes/visibility/google-profile';
import { Ai } from './routes/visibility/ai';
import { Aeo } from './routes/visibility/aeo';
import { Reviews } from './routes/reputation/reviews';
import { ReputationSettings } from './routes/reputation/settings';
import { Inbox } from './routes/pulse/inbox';
import { Leads } from './routes/pulse/leads';
import { Reports } from './routes/reports';
import { Settings } from './routes/settings';
import { Team } from './routes/team';
import { initSentry } from './lib/sentry';
import './styles.css';

// Capture uncaught errors + unhandled rejections to Sentry (disabled-safe).
initSentry('dashboard-spa');

// Analytics is lazy-loaded so recharts (~the heaviest dependency) lands in its
// own chunk instead of the main bundle (which already trips the 500KB warning).
// WP1 Task 10: moved from the retired `routes/site-analytics.tsx` into
// `routes/pulse/analytics.tsx` — same code-splitting, new home.
const PulseAnalytics = lazy(() =>
  import('./routes/pulse/analytics').then((m) => ({ default: m.Analytics })),
);

// Survey analytics (Phase 4) — lazy so recharts lands in its own chunk (the
// eager SurveysList carries no charts).
const SurveyAnalytics = lazy(() =>
  import('./features/surveys/survey-analytics').then((m) => ({ default: m.SurveyAnalytics })),
);

// Survey builder (Phase 5) — lazy so @dnd-kit lands in its own chunk (the eager
// SurveysList stays light).
const SurveyBuilder = lazy(() =>
  import('./features/surveys/survey-builder').then((m) => ({ default: m.SurveyBuilder })),
);

// Locations roll-up dashboard (multi-location phase, Task 8) — lazy for
// consistent code-splitting (it lazy-loads maplibre internally too, same as
// SiteRank, but the route component itself should still land in its own
// chunk rather than the main bundle).
const Locations = lazy(() =>
  import('./routes/locations').then((m) => ({ default: m.Locations })),
);

// Search Console screen (Phase 1) — "/app/b/:siteId/visibility/search". Lazy
// rather than eager like its Visibility siblings: it is a leaf screen most
// customers open rarely, and main.tsx's bundle already trips the 500KB
// warning. It carries no heavy dependency of its own, so unlike `Ai` it does
// not need a second inner lazy layer.
const Search = lazy(() =>
  import('./routes/visibility/search').then((m) => ({ default: m.Search })),
);

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');

createRoot(root).render(
  <StrictMode>
    {/* Top-level last resort (WP8 stage-1 MUST): if a crash happens outside
        any route's own error boundary — e.g. inside PanelShell's own body,
        before it ever reaches the inner ErrorBoundary in its render — this
        is what keeps the tab from going fully blank. No `key` here: this
        boundary sits outside BrowserRouter (no useLocation available), and
        as a true last resort a full reload is the only recovery anyway. */}
    <ErrorBoundary>
      <BrowserRouter>
      <Routes>
        <Route path="/app" element={<App />}>
          {/* Smart landing (WP1 Task 5): fetches sites and forwards into the
              panel shell instead of showing the old site-picker grid. The
              multi-site picker itself (sites-list.tsx) is retired as of WP1
              Task 15 — the switcher accordion in panel-shell.tsx (T5) is the
              canonical multi-site entry point now, and the free-tier "can't
              create another site" nudge that used to render on this screen
              lives in site-new.tsx's own UpgradeGate (fires when a gated org
              actually tries to create a site via appRoutes.start(), same
              destination the switcher's "Add a business" link uses). Bare
              "sites" now falls through to LegacyRedirect, same as every
              other retired /app/sites/* shape (route-map.ts's legacyRedirect
              has no pattern for it, so it lands on the "unrecognised shape"
              fallback → /app). */}
          <Route index element={<AppIndex />} />
          <Route path="sites" element={<LegacyRedirect />} />
          {/* New target for route-map's appRoutes.start() ("/app/new") —
              reuses the existing site-creation screen as-is; a dedicated
              "add a business" flow can replace this in a later task. */}
          {/* T1.6B owns /app/start; until then the old create screen keeps its
              path and /app/new redirects there via legacyRedirect. */}
          <Route path="start" element={<SiteNew />} />
          <Route path="new" element={<LegacyRedirect />} />
          {/* Legacy shape — rewrite the URL to the canonical /app/new instead
              of rendering the same screen under the retired /app/sites/*
              prefix (wp8 fix-forward list, 2026-08-21). */}
          <Route path="sites/new" element={<LegacyRedirect />} />
          {/* sites/:id/forms and sites/:id/analytics were removed here in
              WP1 Task 10 — their new homes are pulse/inbox and
              pulse/analytics under /app/b/:siteId below. The old paths now
              fall through to the /app/sites/* catch-all (LegacyRedirect),
              which already maps both (route-map.ts's legacyRedirect). */}
          {/* T1.6A: business-level screens now name their Business. The old
              tenant-free paths fall through to /app, which resolves one. */}
          <Route path="settings" element={<LegacyRedirect />} />
          {/* Fix round 1 (error-boundary-review.md, finding (2)): Locations
              self-mounts PanelShell internally (routes/locations.tsx) rather
              than being nested under BusinessLayout, so its <Suspense> sits
              OUTSIDE any shell/boundary until the chunk loads — a stale-chunk
              404 here used to reach only the top-level no-key last-resort
              boundary, blanking the whole app. RouteBoundary gives it its
              own keyed, resettable fallback (see error-boundary.tsx). */}
          {/* T1.6B2: these three used to RENDER the real screen here, under a
              path that names no Business. Every one of them — and the
              PanelShell they mount — now addresses the server as
              `/api/b/{businessId}/…`, so with no `:businessId` to read they
              threw out of the effect and the owner got an error boundary
              instead of their surveys. The canonical routes are registered
              below under `/app/b/:businessId`; an old link to the tenant-free
              shape is a LEGACY URL, and legacy URLs go through LegacyRedirect
              like every other one. It cannot resolve a survey id to a
              Business without a server lookup that does not exist, so it
              lands the visitor on `/app`, which then resolves normally —
              their dashboard, not a crash. */}
          <Route path="locations" element={<LegacyRedirect />} />
          <Route path="surveys" element={<LegacyRedirect />} />
          <Route path="surveys/:id" element={<LegacyRedirect />} />
          <Route path="surveys/:id/edit" element={<LegacyRedirect />} />
          <Route path="billing" element={<LegacyRedirect />} />
          {/* PageEdit is a standalone full-screen editor (its own sticky
              header + min-h-screen shell, no sidebar) — it was never
              wrapped in the old DashboardShell either, only in this bare
              `<App>` layout (min-h-full div, no chrome). WP1 Task 7 moves
              it from the old `sites/:id/pages/:pageId/edit` to the new
              nested-URL shape while keeping the SAME wrapping context —
              it stays a child of `<App>`, deliberately NOT nested inside
              BusinessLayout/PanelShell (that would wrap it in sidebar
              chrome it isn't designed for). */}
          <Route
            path="b/:businessId/l/:locationId/foundation/website/pages/:pageId/edit"
            element={<PageEdit />}
          />
          {/* Operator demos moved to manage.pageboss.com (Phase 3C); the
              worker 302s /app/admin/demos there on a full-page load. */}
          <Route path="*" element={<NotFound />} />
        </Route>
        {/* Legacy `/app/sites/*` catch-all (WP1 Task 5, T2's legacyRedirect).
            React Router ranks an exact/dynamic match above a splat, so every
            still-mounted route above (sites/:id/forms, sites/:id/analytics)
            keeps winning unchanged — this only catches a shape none of them
            cover, forwarding it into the new /app/b/:siteId layout below.
            `sites/:id/reviews` moved out of the still-mounted list in Task 9
            (its screen is now reputation/reviews.tsx below), so it now falls
            through to this catch-all — route-map.ts's legacyRedirect already
            has a `/^\/app\/sites\/([^/]+)\/reviews$/` pattern pinned by
            lib/__tests__/route-map.test.ts, so the redirect is unaffected. */}
        <Route path="/app/sites/*" element={<LegacyRedirect />} />
        {/* New four-layer panel shell (WP1 Task 5). The overview screen
            (path-engine wired to live signals) landed in Task 6; the
            Foundation layer screens landed in Task 7; the Visibility layer
            landed in Task 8; remaining child routes land in Tasks 9-11. */}
        {/* T1.6A canonical Location routes: both ids in the URL, mirroring the
            server's /api/b/:businessId/l/:locationId. */}
        <Route path="/app/b/:businessId/l/:locationId" element={<BusinessLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<Overview />} />
          {/* Onboarding wizard shell (WP3 Task 11) — composes the four step
              bodies (Tasks 8-10) inside WizardStep chrome; owns resume/
              skip/finish. Reached from the overview's SetupChecklist "Go →"
              links and from claim-finish's post-claim redirect chain. */}
          <Route path="setup" element={<Setup />} />
          {/* Layer landing screens (WP1 Task 11) — ONE parameterized route
              for the four layer keys (foundation/visibility/reputation/
              pulse), matching route-map.ts's locationRoutes.layer(businessId, ). react-router
              ranks this static-segment-count match below every literal
              two-segment route further down (e.g. "foundation/business-
              info") and below the literal "reports" route, so none of those
              are shadowed — `:layer` only ever wins for a bare
              /app/b/:siteId/{foundation,visibility,reputation,pulse} hit.
              This still matches ANY single segment though (e.g. a typo'd
              /app/b/:siteId/foo) — LayerLanding itself (not this route
              config) is what rejects an unrecognized value, redirecting to
              this site's overview rather than rendering blank (fix round 1,
              MEDIUM-1). */}
          <Route path=":layer" element={<LayerLanding />} />
          {/* Foundation layer (WP1 Task 7) — moved from site-detail.tsx /
              site-business-info.tsx / site-domain.tsx / page-edit.tsx's old
              flat /app/sites/:id/* mounts. `foundation/website` and
              `foundation/website/domain` both mount the same `Website`
              component, which renders Domain as a nested tab of Website
              (routes.website / routes.websiteDomain in route-map.ts). */}
          <Route path="foundation/business-info" element={<BusinessInfo />} />
          <Route path="foundation/website" element={<Website />} />
          <Route path="foundation/website/domain" element={<Website />} />
          {/* Domain & mailbox wizard (WP3b, D4 — CONSOLIDATION A7) — UI
              orchestration over the existing domain/mailbox APIs, reached
              from the locked Mailboxes nav item and the domain panel's
              "start" CTA (routes.domainWizard in route-map.ts). */}
          <Route path="foundation/website/domain/setup" element={<DomainWizard />} />
          <Route path="foundation/mailboxes" element={<Mailboxes />} />
          <Route path="foundation/get-found-ready" element={<GetFoundReady />} />
          {/* Visibility layer (WP1 Task 8) — moved from the old flat
              /app/sites/:id/{gbp,rank,ai-visibility,get-found} mounts.
              `visibility/google-profile{,/content,/map}` all mount the same
              `GoogleProfile` component, which renders Content/Map as tabs of
              Profile the same way `foundation/website{,/domain}` already
              does for Website/Domain (routes.googleProfile /
              routes.googleProfileContent / routes.googleProfileMap in
              route-map.ts). `visibility/ai{,/:engine}` both mount `Ai`, a
              thin wrapper that lazily mounts SiteAiVisibility (same
              code-splitting SiteAiVisibility had directly in this file
              pre-Task-8) — the optional `:engine` param is read inside
              SiteAiVisibility itself. `visibility/aeo` mounts `Aeo`, same
              thin-wrapper pattern, lazily mounting SiteGetFound (which now
              also renders the AEO audit card — see its file header). */}
          <Route path="visibility/google-profile" element={<GoogleProfile />} />
          <Route path="visibility/google-profile/content" element={<GoogleProfile />} />
          <Route path="visibility/google-profile/map" element={<GoogleProfile />} />
          <Route path="visibility/ai" element={<Ai />} />
          <Route path="visibility/ai/:engine" element={<Ai />} />
          <Route path="visibility/aeo" element={<Aeo />} />
          {/* Search Console (Phase 1). Sits under BusinessLayout like the
              rest of the Visibility layer, so a stale-chunk failure inside
              the lazy import reaches the shell's own ErrorBoundary — same
              arrangement as `pulse/analytics` below. */}
          <Route
            path="visibility/search"
            element={
              <Suspense fallback={null}>
                <Search />
              </Suspense>
            }
          />
          {/* Reputation layer (WP1 Task 9) — `reputation/reviews` moves the
              old flat `sites/:id/reviews` (site-reviews.tsx's review-request
              velocity engine, kept intact as an "Ask for reviews" section)
              plus the GBP reviews list that used to live in the pre-Task-8
              site-gbp.tsx / components/gbp/connected-panels.tsx, now read
              through the shared `useGbpData` hook (Task 8). Operator
              decision D1: review requests never got their own nav item, so
              they fold into this one screen — see routes/reputation/
              reviews.tsx's file header. `reputation/settings` is new: the
              orphaned review link/QR card (components/review-link-card.tsx,
              unmounted since site-detail.tsx's retirement) plus a plain
              explainer of the reply-approval rule. */}
          <Route path="reputation/reviews" element={<Reviews />} />
          <Route path="reputation/settings" element={<ReputationSettings />} />
          {/* Pulse layer + Reports (WP1 Task 10) — `pulse/inbox` moves the
              old flat `sites/:id/forms` (site-forms.tsx, retired) verbatim
              minus the DashboardShell/back-link chrome the layer-shell now
              supplies. `pulse/leads` is new: a thin, read-only structured
              view over the SAME GET /api/forms store Inbox reads (spec §7,
              "two views, one underlying store"). `pulse/analytics` moves the
              old flat `sites/:id/analytics` (site-analytics.tsx, retired),
              keeping its recharts code-split, and fixes the gate to key off
              the server's 402 {gated:true} instead of a client-side tier
              guess. `reports` is new: a landing page linking to the rank/AI/
              AEO reports that already live elsewhere, plus an honest "coming
              with your weekly email" note for the WP5 digest. */}
          <Route path="pulse/inbox" element={<Inbox />} />
          <Route path="pulse/leads" element={<Leads />} />
          <Route
            path="pulse/analytics"
            element={
              <Suspense fallback={null}>
                <PulseAnalytics />
              </Suspense>
            }
          />
          <Route path="reports" element={<Reports />} />
        </Route>
        {/* T1.6A canonical BUSINESS routes: they name a Business and no Location. */}
        <Route path="/app/b/:businessId" element={<App />}>
          <Route path="settings" element={<Settings />} />
          <Route path="team" element={<Team />} />
          <Route path="billing" element={<Billing />} />
          <Route path="surveys" element={<SurveysList />} />
          <Route
            path="surveys/:id"
            element={
              <RouteBoundary>
                <Suspense fallback={null}>
                  <SurveyAnalytics />
                </Suspense>
              </RouteBoundary>
            }
          />
          <Route
            path="surveys/:id/edit"
            element={
              <RouteBoundary>
                <Suspense fallback={null}>
                  <SurveyBuilder />
                </Suspense>
              </RouteBoundary>
            }
          />
          <Route
            path="locations"
            element={
              <RouteBoundary>
                <Suspense fallback={null}>
                  <Locations />
                </Suspense>
              </RouteBoundary>
            }
          />
          <Route path="mailboxes" element={<Mailboxes />} />
          {/* Anything else under /app/b/:id that is not `l/…` and not a
              business tail is an OLD Location bookmark — translated through
              the membership-checked server lookup, never guessed. */}
          <Route path="*" element={<LegacyRedirect />} />
        </Route>
        <Route path="/claim/:slug" element={<Claim />} />
        {/* PB-002: the team invitation link (worker serves this shell for a browser navigation to /invite/:token) */}
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
